import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

import {
	countOffersMatchingPlan,
	countOffersMatchingPlanWithLoadingsOnly,
	countOffersMatchingPlanWithExclusionsOnly,
	countOffersMatchingPlanWithLoadingsAndExclusions,
	countOffersMatchingPlanWithRejectedStatus
} from "./helpers.js"

/**
 *  THINGS TO NOTE:
 * An offer in this context === a proposal inside a case since for asia, a proposal SHOULD only have 1 offer
 */

const header = [
	[
		"Template 01 - Number of applications by five underwriting/ re-underwriting results, by age and sex of Insured Person"
	],
	["Age", "Sex", "Standard Plan", "", "", "", "", "Flexi Plan"],
	[
		"",
		"",
		"Standard Offer",
		"Offer with Premium Loading Only",
		"Offer with CBE only",
		"Offer with Premium Loading and CBE",
		"Decline",
		"Standard Offer",
		"Offer with Premium Loading Only",
		"Offer with CBE only",
		"Offer with Premium Loading and CBE",
		"Decline"
	]
]

// Return calculated age
const getAge = item => {
	const dateOfBirth = item?.insureds?.[0]?.dateOfBirth
	const currentYear = dayjs().year()
	const yearOfBirth = dayjs(dateOfBirth).year()
	return currentYear - yearOfBirth
}

const rows = cases => {
	let output = []
	for (let i = 1; i < 100; i++) {
		output.push([
			i,
			"M",
			// standard plans and male
			countStandardOffer(i, "male", cases, "S00027"),
			countOfferWithLoadings(i, "male", cases, "S00027"),
			countOfferWithExclusions(i, "male", cases, "S00027"),
			countOfferWithLoadingsAndExclusions(i, "male", cases, "S00027"),
			countDeclinedOffers(i, "male", cases, "S00027"),
			// flexi plans and male
			countStandardOffer(i, "male", cases, "F00038"),
			countOfferWithLoadings(i, "male", cases, "F00038"),
			countOfferWithExclusions(i, "male", cases, "F00038"),
			countOfferWithLoadingsAndExclusions(i, "male", cases, "F00038"),
			countDeclinedOffers(i, "male", cases, "F00038")
		])
	}
	for (let j = 1; j < 100; j++) {
		output.push([
			j,
			"F",
			// standard plans and female
			countStandardOffer(j, "female", cases, "S00027"),
			countOfferWithLoadings(j, "female", cases, "S00027"),
			countOfferWithExclusions(j, "female", cases, "S00027"),
			countOfferWithLoadingsAndExclusions(j, "female", cases, "S00027"),
			countDeclinedOffers(j, "female", cases, "S00027"),
			// flexi plans and female
			countStandardOffer(j, "female", cases, "F00038"),
			countOfferWithLoadings(j, "female", cases, "F00038"),
			countOfferWithExclusions(j, "female", cases, "F00038"),
			countOfferWithLoadingsAndExclusions(j, "female", cases, "F00038"),
			countDeclinedOffers(j, "female", cases, "F00038")
		])
	}
	return output
}

const countStandardOffer = (age, gender, cases, plan) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countOffersMatchingPlan(item?.proposals, plan)
		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const countOfferWithLoadings = (age, gender, cases, plan) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countOffersMatchingPlanWithLoadingsOnly(
			item?.proposals,
			plan
		)

		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const countOfferWithExclusions = (age, gender, cases, plan) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countOffersMatchingPlanWithExclusionsOnly(
			item?.proposals,
			plan
		)
		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const countOfferWithLoadingsAndExclusions = (age, gender, cases, plan) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countOffersMatchingPlanWithLoadingsAndExclusions(
			item?.proposals,
			plan
		)
		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const countDeclinedOffers = (age, gender, cases, plan) => {
	const filteredCases = cases.map(item => {
		let insuredAge = getAge(item)
		let insuredGender = item?.insureds?.[0]?.gender
		let offersCount = countOffersMatchingPlanWithRejectedStatus(
			item?.proposals,
			plan
		)
		if (insuredAge === age && insuredGender === gender) {
			return offersCount
		} else return 0
	})
	return filteredCases?.reduce((total, item) => {
		return (total += item)
	}, 0)
}

const fetchData = async filterPayload => {
	const query = gql`
		query fetchTemplate01Payload($caseWhere: caseWhere) {
			cases(where: $caseWhere) {
				list {
					id
					insureds {
						... on individual {
							dateOfBirth
							gender
						}
					}
					proposals {
						id
						status
						basket {
							product {
								productId {
									plan
								}
							}
							premium {
								loadings {
									id
								}
							}
							exclusions {
								id
							}
						}
					}
				}
			}
		}
	`

	const variables = {
		caseWhere: filterPayload
	}

	const res = await api({ query, variables })

	return res?.data?.cases?.list
}

export default async filterPayload => {
	const fetchedData = await fetchData(filterPayload)
	const workSheetData = [...header, ...rows(fetchedData)]
	const workSheetColumns = header[2].map(item => {
		return { wpx: 100 }
	})
	const workSheetMerges = [
		{
			s: { r: 0, c: 0 },
			e: { r: 0, c: 12 }
		},
		{
			s: { r: 1, c: 0 },
			e: { r: 2, c: 0 }
		},
		{
			s: { r: 1, c: 1 },
			e: { r: 2, c: 1 }
		},
		{
			s: { r: 1, c: 2 },
			e: { r: 1, c: 6 }
		},
		{
			s: { r: 1, c: 7 },
			e: { r: 1, c: 11 }
		}
	]
	return { workSheetData, workSheetColumns, workSheetMerges }
}
